import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsumerAuthContext = createContext(null);
export const useConsumerAuth = () => useContext(ConsumerAuthContext);

export function ConsumerAuthProvider({ children }) {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Capture a referral code from the invite link (?ref=CODE) before anything else.
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      localStorage.setItem("airpog_ref", ref);
      const url = new URL(window.location.href);
      url.searchParams.delete("ref");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
    // CRITICAL: If returning from OAuth callback, skip the /me check; the callback establishes the session first.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    // 🧹 Fresh visitors (never logged in) skip the session poll entirely — no 401 noise.
    if (localStorage.getItem("airpog_was_pogr") !== "1") { setDriver(null); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/consumer/auth/me`, { withCredentials: true });
      setDriver(r.data);
      try { localStorage.setItem("airpog_was_pogr", "1"); } catch {}
    } catch {
      setDriver(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // 🔒 Link the browser's Terms & Privacy acceptance to this account (paper trail per user).
  useEffect(() => {
    if (!driver?.user_id) return;
    const v = localStorage.getItem("airpog_consent_v");
    if (!v || localStorage.getItem(`airpog_consent_user_${driver.user_id}`) === v) return;
    axios.post(`${API}/consent`, { anon_id: localStorage.getItem("airpog_anon_id"), intent: "account_link" }, { withCredentials: true })
      .then(() => localStorage.setItem(`airpog_consent_user_${driver.user_id}`, v))
      .catch(() => {});
  }, [driver]);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/consumer/auth/logout`, {}, { withCredentials: true }); } catch {}
    setDriver(null);
    try { window.dispatchEvent(new Event("airpog:auth-changed")); } catch {}
  };

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/consumer/auth/me`, { withCredentials: true });
      setDriver(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  const toggleFavorite = async (bizId, meta) => {
    const r = await axios.post(`${API}/consumer/favorites/${encodeURIComponent(bizId)}`, meta || {}, { withCredentials: true });
    setDriver((d) => (d ? { ...d, favorites: r.data.favorites } : d));
    return r.data.favorites;
  };

  const startPlusCheckout = async () => {
    const r = await axios.post(`${API}/consumer/checkout`, { origin_url: window.location.origin }, { withCredentials: true });
    window.location.href = r.data.url;
  };

  const updatePrivacy = async (payload) => {
    const r = await axios.post(`${API}/consumer/privacy`, payload, { withCredentials: true });
    setDriver(r.data);
    return r.data;
  };

  return (
    <ConsumerAuthContext.Provider value={{ driver, loading, loginGoogle, logout, refresh, toggleFavorite, startPlusCheckout, updatePrivacy }}>
      {children}
    </ConsumerAuthContext.Provider>
  );
}

export function ConsumerAuthCallback() {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const finish = () => window.location.replace("/");
    if (!m) { finish(); return; }
    const ref = localStorage.getItem("airpog_ref");
    const headers = { "X-Session-ID": m[1] };
    if (ref) headers["X-Referral-Code"] = ref;
    axios.post(`${API}/consumer/auth/session`, {}, { headers, withCredentials: true })
      .then(() => { localStorage.removeItem("airpog_ref"); try { localStorage.setItem("airpog_was_pogr", "1"); } catch {} finish(); })
      .catch(finish);
  }, []);
  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <Loader2 className="w-7 h-7 animate-spin text-sky-400" />
    </div>
  );
}
